import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Globe, ShieldCheck, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

function statusTone(status) {
  if (status === 'active') return 'bg-emerald-100 text-emerald-800';
  if (status === 'verified') return 'bg-sky-100 text-sky-800';
  if (status === 'pending') return 'bg-amber-100 text-amber-800';
  if (status === 'failed') return 'bg-rose-100 text-rose-800';
  if (status === 'disabled') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

export default function AdminCustomDomains() {
  const [domains, setDomains] = useState([]);

  const load = async () => {
    try {
      const res = await api.get('/admin/custom-domains');
      setDomains(res.data.domains || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load custom domains');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = async (domain, payload, successMessage) => {
    try {
      await api.patch(`/admin/custom-domains/${domain.id}`, payload);
      toast.success(successMessage);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update domain');
    }
  };

  const verify = async (domain) => {
    try {
      await api.patch(`/admin/custom-domains/${domain.id}`, { status: 'active' });
      toast.success('Domain activated');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate domain');
    }
  };

  const remove = async (domain) => {
    try {
      await api.delete(`/admin/custom-domains/${domain.id}`);
      toast.success('Domain removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete domain');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Custom Domains</h1>
        <p className="text-slate-500 text-sm mt-0.5">Review and manage store domain mappings and DNS verification status.</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Globe size={18} /> Domain Mappings
          </CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={load}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Token</TableHead>
                <TableHead>Created</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.length ? domains.map((domain) => (
                <TableRow key={domain.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">{domain.domain}</p>
                      <p className="text-xs text-slate-500">Target: {domain.dns_target || 'shop.jjuma.com'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{domain.owner_name || domain.owner_email || domain.user_id || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{domain.store_name || domain.store_username || '-'}</TableCell>
                  <TableCell><Badge className={statusTone(domain.status)}>{domain.status}</Badge></TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{domain.verification_token || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{domain.created_at ? new Date(domain.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => verify(domain)} className="border-sky-200 text-sky-700 hover:bg-sky-50">
                      <ShieldCheck size={14} className="mr-1" /> Activate
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => update(domain, { status: domain.status === 'disabled' ? 'active' : 'disabled' }, domain.status === 'disabled' ? 'Domain enabled' : 'Domain disabled')}>
                      {domain.status === 'disabled' ? 'Enable' : 'Disable'}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => remove(domain)} className="border-rose-200 text-rose-700 hover:bg-rose-50">
                      <Trash2 size={14} className="mr-1" /> Delete
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8">
                    No custom domains have been configured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
